import React, { useMemo } from 'react';
import { Checkbox, FormControlLabel, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedPlural } from 'react-intl';
import classnames from 'classnames';
import { useMessages } from 'modules/intl';
import { ExpandCollapseAllButton } from 'modules/common/components/ExpandCollapseAllButton';
import { SmartDiaryEntry } from '../type';
import { useSmartDiaryCheckboxIcons } from '../hooks/useSmartDiaryCheckboxIcons';
import { useSmartDiaryTotals } from './hooks';

type SmartDiaryListHeaderProps = {
  smartEntries: SmartDiaryEntry[];
  onExpandCollapseAll: (state: boolean) => void;
  expandCollapseAllId: string;
  className?: string;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 0.125, 0, 3.375),
    color: theme.palette.grey[900]
  },
  durationContainer: {
    display: 'flex',
    paddingRight: theme.spacing(1)
  },
  duration: {
    alignSelf: 'center',
    ...theme.typography.body2,
    paddingRight: theme.spacing(0)
  }
}));

const useCheckBoxStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.grey[400],
    padding: theme.spacing(0.875, 1),
    '&:hover': {
      color: theme.palette.grey[500]
    },
    '&.Mui-checked': {
      color: theme.palette.grey[700]
    },
    '&.MuiCheckbox-indeterminate': {
      color: theme.palette.grey[700]
    },
    '&.Mui-checked:hover': {
      color: theme.palette.grey[700]
    }
  },
  disabled: {
    opacity: 0.4
  }
}));

const useCheckBoxControlStyles = makeStyles((theme) => ({
  label: {
    color: ({ checkedCount }: { checkedCount: number }) =>
      checkedCount === 0
        ? theme.palette.text.secondary
        : theme.palette.text.primary,
    ...theme.typography.body2,
    fontStyle: ({ checkedCount }: { checkedCount: number }) =>
      checkedCount === 0 ? 'italic' : 'normal',
    paddingLeft: theme.spacing(0.5)
  }
}));

const checkBoxInputProps = {
  'data-testid': 'clickable_smart-diary-selected-checkbox'
} as any;
const messageIds = [
  'smartDiary.selection.zero',
  'smartDiary.selection.one',
  'smartDiary.selection.more',
  'smartDiary.total'
];
export const SmartDiaryListHeader: React.FC<SmartDiaryListHeaderProps> = ({
  smartEntries,
  onExpandCollapseAll,
  expandCollapseAllId,
  className
}: SmartDiaryListHeaderProps) => {
  const smartEntryMap = useMemo(
    () =>
      smartEntries.reduce((acc, value) => {
        if (value.canBeSynced) {
          return { ...acc, [value.id]: value };
        }
        return acc;
      }, {} as Record<string, SmartDiaryEntry>),
    [smartEntries]
  );

  const {
    checked,
    indeterminate,
    checkedCount,
    totalDuration,
    onChange,
    disabled
  } = useSmartDiaryTotals(smartEntryMap);
  const classes = useStyles();
  const checkBoxStyles = useCheckBoxStyles();
  const checkBoxControlStyles = useCheckBoxControlStyles({ checkedCount });
  const messageValues = useMemo(
    () => ({
      count: checkedCount
    }),
    [checkedCount]
  );
  const iconProps = useSmartDiaryCheckboxIcons();
  const messages = useMessages(messageIds, messageValues);
  const checkBoxLabel = useMemo(() => {
    if (!checkedCount) {
      return messages['smartDiary.selection.zero'];
    }
    return (
      <FormattedPlural
        one={messages['smartDiary.selection.one']}
        other={messages['smartDiary.selection.more']}
        value={checkedCount}
      />
    );
  }, [checkedCount, messages]);

  return (
    <div
      className={classnames(classes.root, className)}
      data-testid="smart-diary-total"
    >
      <FormControlLabel
        classes={checkBoxControlStyles}
        labelPlacement="end"
        control={useMemo(
          () => (
            <Checkbox
              classes={checkBoxStyles}
              data-testid="smart-diary-selected-checkbox"
              color="default"
              checked={Boolean(checked)}
              indeterminate={indeterminate}
              onChange={onChange}
              inputProps={checkBoxInputProps}
              disabled={disabled}
              {...iconProps}
            />
          ),
          [
            checkBoxStyles,
            checked,
            disabled,
            iconProps,
            indeterminate,
            onChange
          ]
        )}
        label={checkBoxLabel}
      />
      <div className={classes.durationContainer}>
        <Typography
          className={classes.duration}
          data-testid="smart-diary-total-duration"
          variant="body1"
        >
          {totalDuration}
        </Typography>
        <ExpandCollapseAllButton
          id={expandCollapseAllId}
          onExpandCollapseAll={onExpandCollapseAll}
        />
      </div>
    </div>
  );
};
